import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LayoutDashboard,
    Users,
    FileText,
    Settings,
    Bell,
    Search,
    Menu,
    X,
    ChevronRight,
    ChevronDown,
    LogOut,
    Wallet,
    Briefcase,
    FileCheck,
    Package,
    Calendar,
    MessageSquare,
    User,
    Shield,
    TrendingUp,
    CreditCard,
    PieChart,
    List,
    Filter,
    BarChart2,
    Gift,
    Award,
    BarChart3,
    UserCog,
    PlusCircle,
    ClipboardList,
    Eye,
    ListOrdered,
    Contact,
    UserPlus,
    ListChecks,
    History,
    Tag,
    Folder,
    Contact2,
    WalletCards,
    CircleDollarSign,
    BarChart4,
    DollarSign,
    Activity,
} from "lucide-react";
import { BiSupport } from "react-icons/bi";
import { BsCash } from "react-icons/bs";
import { useAuth } from "@/hooks/useAuth";
import { useNotification } from "@/contexts/notification/NotificationContext";
import AdminCreateEntity from "@/components/static/auth/AdminCreateEntity";

// --- Components ---

const SidebarItem = ({
    icon,
    label,
    active,
    onClick,
    isCollapsed,
    notificationCount,
    priority,
    children,
    isOpen,
    onToggle,
    hasChildren,
}) => {
    return (
        <div className="mb-1">
            <motion.button
                whileHover={{ scale: 1.01, x: 2 }}
                whileTap={{ scale: 0.99 }}
                onClick={hasChildren ? onToggle : onClick}
                className={`relative flex items-center w-full p-2.5 rounded-xl transition-all duration-200 group
          ${active && !hasChildren
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                        : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
                    }
          ${isCollapsed ? "justify-center" : ""}
        `}
            >
                <div className={`relative ${active && !hasChildren ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
                    {icon && React.cloneElement(icon, { size: 20 })}
                    {notificationCount > 0 && isCollapsed && (
                        <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${priority === 'urgent' ? 'bg-red-400' : 'bg-blue-400'}`}></span>
                            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${priority === 'urgent' ? 'bg-red-500' : 'bg-blue-500'}`}></span>
                        </span>
                    )}
                </div>

                {!isCollapsed && (
                    <div className="ml-3 flex-1 flex items-center justify-between overflow-hidden">
                        <span className="text-sm font-medium truncate">{label}</span>
                        <div className="flex items-center gap-2">
                            {notificationCount > 0 && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${priority === 'urgent' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    }`}>
                                    {notificationCount > 99 ? '99+' : notificationCount}
                                </span>
                            )}
                            {hasChildren && (
                                <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                                    <ChevronDown size={14} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {active && !hasChildren && !isCollapsed && (
                    <motion.div
                        layoutId="active-pill"
                        className="absolute left-0 w-1 h-6 bg-white rounded-r-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />
                )}
            </motion.button>

            {/* Dropdown Children */}
            <AnimatePresence>
                {hasChildren && isOpen && !isCollapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden pl-4 mt-1 space-y-1"
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const AdminLayout = ({ children }) => {
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [openSections, setOpenSections] = useState({});
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { unreadCount, categorizedCounts, getCategoryCount } = useNotification();

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (mobile) setSidebarOpen(false);
            else setSidebarOpen(true);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper to get notification priority
    const getHighestPriority = (categoryData) => {
        if (!categoryData) return "low";
        if (categoryData.urgent > 0) return "urgent";
        if (categoryData.high > 0) return "high";
        if (categoryData.medium > 0) return "medium";
        return "low";
    };

    // Navigation Configuration
    const navConfig = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: <LayoutDashboard />,
            path: '/admindashboard/dashboard'
        },
        {
            id: 'notifications',
            label: 'Notifications',
            icon: <Bell />,
            path: '/admindashboard/admin-notifications',
            badge: unreadCount,
            priority: 'urgent'
        },
        {
            id: 'admin-wallet',
            label: 'Admin Wallet',
            icon: <Wallet />,
            path: '/admindashboard/admin-wallet',
            badge: categorizedCounts?.categories?.AdminWallet?.unread || 0,
            priority: getHighestPriority(categorizedCounts?.categories?.AdminWallet)
        },
        {
            id: 'userManagement',
            label: 'Users Management',
            icon: <Users />,
            children: [
                { id: 'all-users', label: 'Users Stats', icon: <Users />, path: '/admindashboard/all-users', badge: categorizedCounts?.categories?.User?.unread, priority: getHighestPriority(categorizedCounts?.categories?.User) },
                { id: 'user-details', label: 'All User Details', icon: <User />, path: '/admindashboard/user-details' },
                { id: 'Kyc-Status', label: 'KYC Status', icon: <ListChecks />, path: '/admindashboard/Kyc-Status', badge: categorizedCounts?.categories?.Kyc?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Kyc) },
                { id: 'user-watchlists', label: 'User Watchlists', icon: <Eye />, path: '/admindashboard/user-watchlists', badge: categorizedCounts?.categories?.Watchlist?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Watchlist) },
                { id: 'user-wallet', label: 'User Wallet', icon: <Wallet />, path: '/admindashboard/user-wallet', badge: categorizedCounts?.categories?.Wallet?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Wallet) },
                { id: 'user-Transactions', label: 'User Transactions', icon: <History />, path: '/admindashboard/user-Transactions', badge: categorizedCounts?.categories?.Transaction?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Transaction) },
                { id: 'user-portfolios', label: 'User Portfolios', icon: <Briefcase />, path: '/admindashboard/user-portfolios', badge: categorizedCounts?.categories?.Portfolio?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Portfolio) },
                { id: 'user-holdings', label: 'User Holdings', icon: <PieChart />, path: '/admindashboard/user-holdings', badge: categorizedCounts?.categories?.Holdings?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Holdings) },
                { id: 'user-order-request', label: 'User Order Request', icon: <ListOrdered />, path: '/admindashboard/user-order-request', badge: categorizedCounts?.categories?.Order?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Order) },
            ]
        },
        {
            id: 'productManagement',
            label: 'Product Management',
            icon: <Package />,
            children: [
                { id: 'product-list', label: 'Product List', icon: <List />, path: '/admindashboard/product-list' },
                { id: 'product-quantity', label: 'Product Quantity', icon: <ListOrdered />, path: '/admindashboard/product-quantity' },
                { id: 'product-sell', label: 'Product Sell', icon: <TrendingUp />, path: '/admindashboard/product-sell' },
                // { id: 'product-chart', label: 'Product Chart', icon: <BarChart2 />, path: '/admindashboard/product-chart' },
            ]
        },
        {
            id: 'expertManagement',
            label: 'Expert Management',
            icon: <UserCog />,
            children: [
                { id: 'expert-list', label: 'Expert List', icon: <Users />, path: '/admindashboard/expert-list', badge: categorizedCounts?.categories?.Expert?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Expert) },
                { id: 'appointment-list', label: 'Appointment List', icon: <Calendar />, path: '/admindashboard/appointment-list', badge: categorizedCounts?.categories?.Appointment?.unread, priority: getHighestPriority(categorizedCounts?.categories?.Appointment) },
                { id: 'create-expert', label: 'Create Expert', icon: <PlusCircle />, path: '/admindashboard/create-expert' },
            ]
        },
        {
            id: 'blogManagement',
            label: 'Blog Management',
            icon: <FileText />,
            children: [
                { id: 'blog-posts', label: 'Blog Posts', icon: <FileText />, path: '/admindashboard/blog-posts' },
                { id: 'blog-categories', label: 'Blog Categories', icon: <Folder />, path: '/admindashboard/blog-categories' },
                { id: 'blog-tags', label: 'Blog Tags', icon: <Tag />, path: '/admindashboard/blog-tags' },
                { id: 'blog-stats', label: 'Blog Stats', icon: <BarChart3 />, path: '/admindashboard/blog-stats' },
            ]
        },
        {
            id: 'referralManagement',
            label: 'Referral Management',
            icon: <Gift />,
            children: [
                { id: 'referral-list', label: 'Referral List', icon: <List />, path: '/admindashboard/referral-list', badge: categorizedCounts?.categories?.ReferralTracking?.unread, priority: getHighestPriority(categorizedCounts?.categories?.ReferralTracking) },
                { id: 'referral-stats', label: 'Referral Stats', icon: <BarChart2 />, path: '/admindashboard/referral-stats' },
            ]
        },
        /* {
            id: 'mutualFund',
            label: 'Mutual Fund',
            icon: <CircleDollarSign />,
            children: [
                { id: 'mutual-fund-list', label: 'Mutual Fund List', icon: <List />, path: '/admindashboard/mutual-fund-list' },
                { id: 'mutual-fund-analytics', label: 'Analytics', icon: <BarChart4 />, path: '/admindashboard/mutual-fund-analytics' },
                { id: 'mutual-fund-categories', label: 'Categories', icon: <Folder />, path: '/admindashboard/mutual-fund-categories' },
            ]
        }, */
        {
            id: 'asperoManagement',
            label: 'Aspero Bonds',
            icon: <Activity />,
            children: [
                { id: 'aspero-dashboard', label: 'Aspero Dashboard', icon: <LayoutDashboard />, path: '/admindashboard/aspero-dashboard' },
                { id: 'aspero-investments', label: 'Bond Orders', icon: <History />, path: '/admindashboard/aspero-investments' },
                { id: 'aspero-logs', label: 'Webhook Logs', icon: <ClipboardList />, path: '/admindashboard/aspero-logs' },
            ]
        },
        /* {
            id: 'bond',
            label: 'Internal Bonds',
            icon: <DollarSign />,
            children: [
                { id: 'bond-list', label: 'Bond List', icon: <List />, path: '/admindashboard/bond-list' },
                { id: 'bond-analytics', label: 'Analytics', icon: <BarChart4 />, path: '/admindashboard/bond-analytics' },
                { id: 'bond-issuers', label: 'Issuers', icon: <Users />, path: '/admindashboard/bond-issuers' },
            ]
        }, */
        {
            id: 'postManagement',
            label: 'Post Management',
            icon: <MessageSquare />,
            children: [
                { id: 'create-post', label: 'Create Post', icon: <PlusCircle />, path: '/admindashboard/create-post' },
                { id: 'all-posts', label: 'All Posts', icon: <List />, path: '/admindashboard/all-posts' },
            ]
        },
        {
            id: 'supportManagement',
            label: 'Support Management',
            icon: <BiSupport />,
            children: [
                { id: 'contact-data', label: 'Contact Data', icon: <Contact2 />, path: '/admindashboard/contact-data' },
                { id: 'contact-support', label: 'Contact Support', icon: <Contact />, path: '/admindashboard/contact-support' },
            ]
        },
        {
            id: 'filter-list',
            label: 'Filter List',
            icon: <Filter />,
            path: '/admindashboard/filter-list'
        },
        {
            id: 'admin-profile',
            label: 'Admin Profile',
            icon: <UserCog />,
            path: '/admindashboard/admin-profile'
        }
    ];

    const renderSidebarItems = (items) => {
        return items.map((item) => (
            <SidebarItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={item.path ? location.pathname.includes(item.path) : false}
                onClick={() => {
                    if (item.path) {
                        navigate(item.path);
                        if (isMobile) setSidebarOpen(false);
                    }
                }}
                isCollapsed={!isSidebarOpen && !isMobile}
                notificationCount={item.badge}
                priority={item.priority}
                hasChildren={!!item.children}
                isOpen={openSections[item.id]}
                onToggle={() => toggleSection(item.id)}
            >
                {item.children && renderSidebarItems(item.children)}
            </SidebarItem>
        ));
    };

    return (
        <div className="flex h-screen bg-slate-50 overflow-hidden font-sans text-slate-900">
            {/* Sidebar Backdrop for Mobile */}
            <AnimatePresence>
                {isMobile && isSidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSidebarOpen(false)}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <motion.aside
                initial={false}
                animate={{
                    width: isSidebarOpen ? 280 : 80,
                    x: isMobile && !isSidebarOpen ? -280 : 0
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className={`fixed lg:static inset-y-0 left-0 z-50 bg-[#0F172A] text-white shadow-2xl flex flex-col
            ${isMobile ? 'w-[280px]' : ''}
        `}
            >
                {/* Sidebar Header */}
                <div className="h-20 flex items-center justify-between px-6 border-b border-slate-800/50">
                    <div className={`flex items-center gap-3 ${!isSidebarOpen && !isMobile ? 'justify-center w-full' : ''}`}>
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <Shield className="text-white" size={24} />
                        </div>
                        {(isSidebarOpen || isMobile) && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex flex-col"
                            >
                                <span className="font-bold text-lg tracking-tight">InvestHive</span>
                                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Admin Panel</span>
                            </motion.div>
                        )}
                    </div>
                    {isMobile && (
                        <button onClick={() => setSidebarOpen(false)} className="p-2 text-slate-400 hover:text-white">
                            <X size={20} />
                        </button>
                    )}
                </div>

                {/* Create Entity Button */}
                {(isSidebarOpen || isMobile) && (
                    <div className="px-4 py-4">
                        <button
                            onClick={() => setShowCreateUserModal(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/20"
                        >
                            <UserPlus size={18} />
                            <span>Create New Entity</span>
                        </button>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto px-3 custom-scrollbar space-y-1 pb-6">
                    {renderSidebarItems(navConfig)}
                </div>

                {/* Sidebar Footer */}
                <div className="p-4 border-t border-slate-800/50">
                    <button
                        onClick={logout}
                        className={`flex items-center w-full p-3 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all
                    ${!isSidebarOpen && !isMobile ? 'justify-center' : ''}
                `}
                    >
                        <LogOut size={20} />
                        {(isSidebarOpen || isMobile) && <span className="ml-3 font-medium">Logout</span>}
                    </button>
                </div>
            </motion.aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Top Header */}
                <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-30 px-4 lg:px-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleSidebar}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
                        >
                            <Menu size={24} />
                        </button>

                        {/* Search Bar */}
                        <div className="hidden md:flex items-center relative">
                            <Search className="absolute left-3 text-slate-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search anything..."
                                className="pl-10 pr-4 py-2.5 bg-slate-100/50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 w-64 transition-all"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Notifications */}
                        <button
                            onClick={() => navigate('/admindashboard/admin-notifications')}
                            className="relative p-2.5 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
                        >
                            <Bell size={20} />
                            {unreadCount > 0 && (
                                <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                            )}
                        </button>

                        {/* Profile Dropdown */}
                        <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                            <div className="text-right hidden md:block">
                                <p className="text-sm font-bold text-slate-800">Admin User</p>
                                <p className="text-xs text-slate-500">Super Admin</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/20 ring-2 ring-white">
                                A
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto bg-slate-50 p-4 lg:p-8 custom-scrollbar">
                    <div className="max-w-7xl mx-auto">
                        {children || <Outlet />}
                    </div>
                </main>
            </div>

            {/* Create User Modal */}
            {showCreateUserModal && (
                <AdminCreateEntity onClose={() => setShowCreateUserModal(false)} />
            )}
        </div>
    );
};

export default AdminLayout;
